import React, { useState, useEffect } from 'react';
import Header from '../components/landing/Header';
import TopBar from '../components/landing/TopBar';
import Footer from '../components/landing/Footer';
import EnquiryModal from '../components/landing/EnquiryModal';
import { Microscope, BookOpen, Lightbulb, Award, Globe, FlaskConical } from 'lucide-react';
import api from '../utils/axios';
import getFullUrl from '../utils/formatUrl';

const Research = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isInquiryOpen, setIsInquiryOpen] = useState(false);
    const [content, setContent] = useState({});

    useEffect(() => {
        window.scrollTo(0, 0);
        const handleScroll = () => setIsScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll);
        fetchContent();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const fetchContent = async () => {
        try {
            const res = await api.get('/content');
            const contentMap = {};
            res.data.data.forEach(item => {
                contentMap[item.key] = item.value;
            });
            setContent(contentMap);
        } catch (err) {
            console.error("Failed to load web content", err);
        }
    };

    const stats = [
        { label: 'Research Papers', value: content.research_papers || '1500+', icon: BookOpen },
        { label: 'Patents Filed', value: content.research_patents || '45+', icon: Lightbulb },
        { label: 'External Grants', value: content.research_grants || '12 Cr+', icon: Award },
        { label: 'Research Centers', value: content.research_centers_count || '12', icon: Microscope }
    ];

    return (
        <div className="min-h-screen bg-white font-sans text-slate-900">
            {!isScrolled && <TopBar data={content} />}
            <Header 
                isScrolled={isScrolled} 
                onApply={() => setIsInquiryOpen(true)}
                logo={content.site_logo}
                email={content.contact_email}
                phone={content.contact_phone}
                applyUrl={content.apply_url}
                colleges={content.colleges_list}
                placements={content.placements_list}
                research={content.research_list}
                journals={content.journals_list}
                innovations={content.innovations_list}
            />

            {/* Internal Hero */}
            <header className="relative h-[450px] bg-slate-900 flex items-center overflow-hidden">
                <div className="absolute inset-0 bg-[#003366]/85 z-10"></div>
                <img 
                    src={getFullUrl(content.research_hero_image) || "https://images.unsplash.com/photo-1532094349884-543bc11b234d?q=80&w=2000&auto=format&fit=crop"} 
                    className="absolute inset-0 w-full h-full object-cover"
                    alt="Research & Development"
                />
                <div className="max-w-7xl mx-auto px-4 relative z-20 w-full text-center">
                    <h4 className="text-amber-400 font-black uppercase tracking-[8px] text-xs mb-4">Innovation for Future</h4>
                    <h1 className="text-5xl md:text-7xl font-black text-white uppercase tracking-tighter">
                        Research & <br /><span className="text-[#cc0000]">Development</span>
                    </h1>
                    <div className="h-2 w-24 bg-white mx-auto mt-8"></div>
                </div>
            </header>

            {/* Stats Grid */}
            <section className="relative z-30 -mt-16 max-w-7xl mx-auto px-4">
                <div className="bg-white shadow-2xl rounded-sm grid grid-cols-2 lg:grid-cols-4 border-b-8 border-[#cc0000]">
                    {stats.map((s, i) => (
                        <div key={i} className="p-10 text-center border-r last:border-0 border-slate-100 group">
                            <s.icon size={40} className="mx-auto text-[#003366] mb-6 group-hover:text-[#cc0000] transition-colors" />
                            <div className="text-4xl font-black text-slate-900 tracking-tighter mb-2">{s.value}</div>
                            <div className="text-[10px] font-black text-slate-500 uppercase tracking-[3px]">{s.label}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Introduction */}
            <section className="py-32">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-24 items-center">
                        <div className="space-y-8">
                            <h3 className="text-[#cc0000] font-black uppercase tracking-[5px] text-[11px]">Academic Inquiry</h3>
                            <h2 className="text-5xl font-black uppercase text-slate-900 tracking-tight leading-none">
                                {content.research_intro_title || 'Pushing the Boundaries of Knowledge'}
                            </h2>
                            <p className="text-slate-600 leading-relaxed text-lg font-medium">
                                {content.research_intro_desc || 'At Arya Sabhyata, research is not just an activity but a culture. We empower our faculty and students to solve real-world problems through interdisciplinary collaboration and state-of-the-art infrastructure. From sustainable energy to artificial intelligence, our research centers are hubs of innovation.'}
                            </p>
                            <div className="grid grid-cols-2 gap-8 pt-4">
                                <div className="p-6 bg-slate-50 border-l-4 border-[#003366]">
                                    <Globe className="text-[#003366] mb-4" />
                                    <h4 className="font-black uppercase text-xs mb-2">Global Partnerships</h4>
                                    <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">MOU with 50+ International Universities</p>
                                </div>
                                <div className="p-6 bg-slate-50 border-l-4 border-[#cc0000]">
                                    <FlaskConical className="text-[#cc0000] mb-4" />
                                    <h4 className="font-black uppercase text-xs mb-2">Advanced Labs</h4>
                                    <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Industry Standard Research Equipment</p>
                                </div>
                            </div>
                        </div>
                        <div className="relative">
                            <div className="absolute inset-0 bg-slate-900 rounded-2xl rotate-3 scale-95 opacity-10"></div>
                            <img 
                                src={getFullUrl(content.research_intro_image) || "https://images.unsplash.com/photo-1507413245164-6160d8298b31?q=80&w=1000&auto=format&fit=crop"} 
                                className="rounded-2xl shadow-3xl relative z-10" 
                                alt="Research Lab" 
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Research Priorities */}
            <section className="py-24 bg-slate-900 text-white overflow-hidden relative">
                <div className="absolute right-0 top-0 w-1/2 h-full bg-white opacity-[0.02] -skew-x-12 translate-x-1/2"></div>
                <div className="max-w-7xl mx-auto px-4 relative z-10">
                    <h3 className="text-amber-400 font-black uppercase tracking-[5px] text-[11px] mb-12 text-center">Our Focus Areas</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                        {[
                            { title: 'Artificial Intelligence', desc: 'Predictive modeling, deep learning, and ethical AI frameworks.', icon: Microscope },
                            { title: 'Sustainable Energy', desc: 'Next-gen solar, hydrogen storage, and smart grid systems.', icon: Lightbulb },
                            { title: 'Material Science', desc: 'Nano-composites and structural engineering for the 21st century.', icon: BookOpen }
                        ].map((item, idx) => (
                            <div key={idx} className="p-12 border border-white/10 bg-white/5 backdrop-blur-sm group hover:bg-[#cc0000] transition-colors duration-500">
                                <item.icon size={48} className="text-amber-400 mb-8 group-hover:text-white transition-colors" />
                                <h4 className="text-2xl font-black uppercase mb-4 tracking-tighter">{item.title}</h4>
                                <p className="text-slate-400 text-sm font-medium leading-relaxed group-hover:text-white/80">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <Footer data={content} />

            <EnquiryModal isOpen={isInquiryOpen} onClose={() => setIsInquiryOpen(false)} />
        </div>
    );
};

export default Research;
